import { CustomSelect, SelectOption } from '@consigli/facade';
import { ConflictType } from '@consigli/types';
import clsx from 'clsx';
import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowUpRight, FiMaximize2, FiMinimize2 } from 'react-icons/fi';

import type {
  BidComparisonCell,
  BidComparisonCitation,
  BidComparisonData,
  BidComparisonRow,
} from './bid-comparison-types';

// ADDRESSED means the bid fully covered the item (green); PARTIAL and DEVIATION
// both mean it only partly met it (yellow). Anything else carries no color mark.
const CONFLICT_TONE: Record<string, { cell: string; dot: string; text: string }> = {
  [ConflictType.ADDRESSED]: {
    cell: 'bg-green-50',
    dot: 'bg-green-500',
    text: 'text-green-900',
  },
  [ConflictType.PARTIAL]: {
    cell: 'bg-amber-50',
    dot: 'bg-amber-500',
    text: 'text-amber-900',
  },
  [ConflictType.DEVIATION]: {
    cell: 'bg-amber-50',
    dot: 'bg-amber-500',
    text: 'text-amber-900',
  },
};

const ITEM_WIDTH = 240;
const CATEGORY_WIDTH = 150;
const WEIGHT_WIDTH = 110;
const CONTRACTOR_WIDTH = 300;

const ITEM_LEFT = 0;
const CATEGORY_LEFT = ITEM_WIDTH;
const WEIGHT_LEFT = ITEM_WIDTH + CATEGORY_WIDTH;

// Contractor cells clip to the row height, which the Item column drives. Give
// them a floor of ~two text lines (plus the cell's vertical padding) so a short
// item still leaves the bid text readable; a taller item grows the row as
// before. On a table cell `height` behaves as a minimum, not a fixed height.
const CONTRACTOR_MIN_CELL_HEIGHT = '4.5rem';
const ACTIVE_ROW_BG = 'bg-blue-50/60';

const cellStyle = {
  minWidth: CONTRACTOR_WIDTH,
  width: CONTRACTOR_WIDTH,
  height: CONTRACTOR_MIN_CELL_HEIGHT,
} as const;

const stickyHeaderClass =
  'sticky top-0 z-30 bg-day-light-3 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-day-neutral-subtle';

interface ContractorCellProps {
  cell: BidComparisonCell | undefined;
  rowId: string;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onViewSource?: (citation: BidComparisonCitation, cell: BidComparisonCell, rowId: string) => void;
}

const ContractorCell: FC<ContractorCellProps> = ({
  cell,
  rowId,
  isActive,
  isExpanded,
  onToggle,
  onViewSource,
}) => {
  const { t } = useTranslation();
  const clipRef = useRef<HTMLDivElement>(null);
  // Whether the text is taller than the row height (driven by the Item column).
  // Measured only while collapsed and kept while expanded, so the collapse
  // control stays only on the cells that actually have hidden overflow.
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    if (isExpanded) {
      return;
    }
    const element = clipRef.current;
    if (!element) {
      return;
    }
    const measure = () => setIsOverflowing(element.scrollHeight - element.clientHeight > 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [cell?.value, isExpanded]);

  if (!cell) {
    return (
      <td
        className={clsx(
          'border-l border-gray-100 px-4 py-3 align-top text-gray-400 transition-colors',
          !isActive && 'group-hover:bg-gray-50/70',
        )}
        style={cellStyle}
      >
        —
      </td>
    );
  }

  const tone = cell.amount
    ? CONFLICT_TONE[ConflictType.ADDRESSED]
    : cell.conflictType
      ? CONFLICT_TONE[cell.conflictType]
      : undefined;

  return (
    <td
      className={clsx(
        'relative border-l border-gray-100 align-top transition-colors',
        tone ? tone.cell : !isActive && 'group-hover:bg-gray-50/70',
      )}
      style={cellStyle}
    >
      <div
        className={clsx(
          'flex items-start justify-between gap-2 px-4 py-3',
          !isExpanded && 'absolute inset-0',
        )}
      >
        <div
          ref={clipRef}
          className={clsx('min-w-0 flex-1', !isExpanded && 'h-full overflow-hidden')}
        >
          <p
            className={clsx(
              'flex items-start gap-2 whitespace-pre-line text-sm leading-relaxed text-gray-800',
              tone && `font-medium ${tone.text}`,
            )}
          >
            {tone && (
              <span
                className={clsx('mt-1.5 h-2 w-2 shrink-0 rounded-full', tone.dot)}
                aria-hidden
              />
            )}
            {cell.value}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-center gap-1">
          {cell.citation && (
            <button
              type="button"
              onClick={() => cell.citation && onViewSource?.(cell.citation, cell, rowId)}
              className="rounded p-1 text-day-neutral-subtle hover:bg-day-light-3 hover:text-day-neutral-dark"
              title={t('bid-comparison.view-source', 'View source')}
              aria-label={t('bid-comparison.view-source', 'View source')}
            >
              <FiArrowUpRight size={14} />
            </button>
          )}
          {isOverflowing && (
            <button
              type="button"
              onClick={onToggle}
              className="rounded p-1 text-day-neutral-subtle hover:bg-day-light-3 hover:text-day-neutral-dark"
              title={
                isExpanded
                  ? t('bid-comparison.collapse', 'Collapse')
                  : t('bid-comparison.expand', 'Expand')
              }
              aria-label={
                isExpanded
                  ? t('bid-comparison.collapse', 'Collapse')
                  : t('bid-comparison.expand', 'Expand')
              }
            >
              {isExpanded ? <FiMinimize2 size={14} /> : <FiMaximize2 size={14} />}
            </button>
          )}
        </div>
      </div>
    </td>
  );
};

interface BidComparisonTableProps {
  data: BidComparisonData;
  weights: Record<string, number>;
  activeRowId?: string | null;
  onWeightChange: (rowId: string, value: string) => void;
  onViewSource?: (citation: BidComparisonCitation, cell: BidComparisonCell, rowId: string) => void;
  onViewReference?: (row: BidComparisonRow) => void;
}

export const BidComparisonTable: FC<BidComparisonTableProps> = ({
  data,
  weights,
  activeRowId,
  onWeightChange,
  onViewSource,
  onViewReference,
}) => {
  const { t } = useTranslation();
  const { contractors, rows } = data;

  const [expandedRows, setExpandedRows] = useState<ReadonlySet<string>>(new Set());
  const [activeCategories, setActiveCategories] = useState<ReadonlySet<string>>(new Set());

  const categoryOptions = useMemo<SelectOption[]>(
    () =>
      Array.from(new Set(rows.map((row) => row.category)))
        .sort()
        .map((category) => ({ label: category, value: category })),
    [rows],
  );

  const visibleRows = useMemo(
    () =>
      activeCategories.size === 0 ? rows : rows.filter((row) => activeCategories.has(row.category)),
    [rows, activeCategories],
  );

  const handleCategoryChange = useCallback((options: SelectOption[]) => {
    setActiveCategories(new Set(options.map((option) => option.value.toString())));
  }, []);

  const totalWeight = useMemo(
    () => Object.values(weights).reduce((total, weight) => total + (weight || 0), 0),
    [weights],
  );

  const toggleRow = useCallback((rowId: string) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const cellByContractor = useCallback(
    (cells: BidComparisonCell[], contractorId: string) =>
      cells.find((cell) => cell.contractorId === contractorId),
    [],
  );

  return (
    // `isolate` keeps the table's internal sticky z-indexes (the category filter
    // and the sticky header/columns below) in their own stacking context, so they
    // don't punch through page-level overlays like the Analysis switcher dropdown.
    <div className="flex flex-col gap-3 isolate">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {categoryOptions.length > 1 ? (
          <div className="relative z-50 w-full sm:w-72">
            <CustomSelect
              onChange={(selectedOptions: SelectOption[]): void =>
                handleCategoryChange(selectedOptions)
              }
              isMulti
              isClearable
              placeholder={t('bid-comparison.filter-category', 'Filter by category')}
              options={categoryOptions}
              value={categoryOptions.filter((option) =>
                activeCategories.has(option.value.toString()),
              )}
              className="w-full"
              noOptionsMessage={t('custom-select.no-options')}
            />
          </div>
        ) : (
          <div />
        )}
        <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-day-neutral-subtle">
          <span className="flex items-center gap-1.5">
            <span
              className={clsx('h-2 w-2 rounded-full', CONFLICT_TONE[ConflictType.ADDRESSED].dot)}
              aria-hidden
            />
            {t('bid-comparison.legend-addressed', 'Addressed')}
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className={clsx('h-2 w-2 rounded-full', CONFLICT_TONE[ConflictType.PARTIAL].dot)}
              aria-hidden
            />
            {t('bid-comparison.legend-partially-addressed', 'Partially addressed')}
          </span>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white shadow-sm">
        <table className="border-collapse text-sm" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th
                className={clsx(stickyHeaderClass, 'z-40 border-b border-gray-200')}
                style={{ left: ITEM_LEFT, minWidth: ITEM_WIDTH, width: ITEM_WIDTH }}
              >
                {t('bid-comparison.col-item', 'Item')}
              </th>
              <th
                className={clsx(stickyHeaderClass, 'z-40 border-b border-gray-200')}
                style={{ left: CATEGORY_LEFT, minWidth: CATEGORY_WIDTH, width: CATEGORY_WIDTH }}
              >
                {t('bid-comparison.col-category', 'Category')}
              </th>
              <th
                className={clsx(
                  stickyHeaderClass,
                  'z-40 border-b border-r border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]',
                )}
                style={{ left: WEIGHT_LEFT, minWidth: WEIGHT_WIDTH, width: WEIGHT_WIDTH }}
              >
                {t('bid-comparison.col-weight', 'Weight')}
              </th>
              {contractors.map((contractor) => (
                <th
                  key={contractor.id}
                  className="sticky top-0 z-20 border-b border-l border-gray-200 bg-day-light-3 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-day-neutral-dark"
                  style={{ minWidth: CONTRACTOR_WIDTH, width: CONTRACTOR_WIDTH }}
                >
                  {contractor.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleRows.map((row) => {
              const isExpanded = expandedRows.has(row.id);
              const isActive = activeRowId === row.id;
              const stickyBg = isActive ? ACTIVE_ROW_BG : 'bg-white';
              return (
                <tr key={row.id} className={clsx('group align-top', isActive && ACTIVE_ROW_BG)}>
                  <td
                    className={clsx(
                      'sticky z-10 border-r-0 px-4 py-3 align-top font-medium leading-relaxed text-gray-900 transition-colors',
                      stickyBg,
                      !isActive && 'group-hover:bg-gray-50/70',
                    )}
                    style={{ left: ITEM_LEFT, minWidth: ITEM_WIDTH, width: ITEM_WIDTH }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 flex-1">{row.item}</span>
                      {row.reference && onViewReference && (
                        <button
                          type="button"
                          onClick={() => onViewReference(row)}
                          className="mt-0.5 shrink-0 rounded p-1 text-day-neutral-subtle hover:bg-day-light-3 hover:text-day-neutral-dark"
                          title={t('bid-comparison.view-tender', 'View tender document')}
                          aria-label={t('bid-comparison.view-tender', 'View tender document')}
                        >
                          <FiArrowUpRight size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td
                    className={clsx(
                      'sticky z-10 px-4 py-3 align-top text-gray-600 transition-colors',
                      stickyBg,
                      !isActive && 'group-hover:bg-gray-50/70',
                    )}
                    style={{ left: CATEGORY_LEFT, minWidth: CATEGORY_WIDTH, width: CATEGORY_WIDTH }}
                  >
                    <span className="inline-flex items-center rounded-full bg-day-light-3 px-2.5 py-0.5 text-xs font-medium text-day-neutral-subtle">
                      {row.category}
                    </span>
                  </td>
                  <td
                    className={clsx(
                      'sticky z-10 border-r border-gray-200 px-4 py-3 align-top shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] transition-colors',
                      stickyBg,
                      !isActive && 'group-hover:bg-gray-50/70',
                    )}
                    style={{ left: WEIGHT_LEFT, minWidth: WEIGHT_WIDTH, width: WEIGHT_WIDTH }}
                  >
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={weights[row.id] ?? 0}
                        onChange={(event) => onWeightChange(row.id, event.target.value)}
                        className="w-14 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-day-neutral-dark focus:outline-none"
                        aria-label={t('bid-comparison.weight-for', 'Weight for {{item}}', {
                          item: row.item,
                        })}
                      />
                      <span className="text-xs text-gray-400">%</span>
                    </div>
                  </td>
                  {contractors.map((contractor) => {
                    const cell = cellByContractor(row.cells, contractor.id);
                    return (
                      <ContractorCell
                        key={contractor.id}
                        cell={cell}
                        rowId={row.id}
                        isActive={isActive}
                        isExpanded={isExpanded}
                        onToggle={() => toggleRow(row.id)}
                        onViewSource={onViewSource}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-day-light-3">
              <td
                className="sticky z-10 bg-day-light-3 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-day-neutral-subtle"
                style={{ left: ITEM_LEFT, minWidth: ITEM_WIDTH, width: ITEM_WIDTH }}
              >
                {t('bid-comparison.total-weighting', 'Total weighting')}
              </td>
              <td
                className="sticky z-10 bg-day-light-3"
                style={{ left: CATEGORY_LEFT, minWidth: CATEGORY_WIDTH, width: CATEGORY_WIDTH }}
              />
              <td
                className={clsx(
                  'sticky z-10 border-r border-gray-200 bg-day-light-3 px-4 py-3 text-sm font-semibold shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]',
                  totalWeight === 100 ? 'text-green-700' : 'text-amber-700',
                )}
                style={{ left: WEIGHT_LEFT, minWidth: WEIGHT_WIDTH, width: WEIGHT_WIDTH }}
              >
                {totalWeight}%
              </td>
              <td
                colSpan={contractors.length}
                className="border-l border-gray-200 bg-day-light-3"
              />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
